import { gql } from '@apollo/client';

import { PREVIEW_CARD, PREVIEW_IMAGES, EXTRA_DATA_FIELDS, PREVIEW_ACTION_USER, CORE_USER_FIELDS } from '../gql';

export const GET_USER_CARDS = gql`
  query getUserCards(
    $username: String!
    $offset: Int!
    $type: CardTypeOptions = CARD
    $limit: Int = 10
    $referrerId: String
    $excludedIds: [String] = []
    $sorting: ECardSortingOptions = PICK_DATE_CREATED_DESC
    $refListId: String
    $withImage: Boolean
    $includeQuestionResponse: Boolean = false
  ) {
    allUsers(filters: { username: $username }) {
      totalCount
      items {
        id
        username
        cardId
        pickedCards(
          limit: $limit
          offset: $offset
          sorting: $sorting
          filters: { type: $type, not: { idIn: $excludedIds }, withImage: $withImage }
        ) {
          totalCount
          items {
            ...PreviewCard
            ...PreviewImages
            extraData(limit: 3) {
              ...ExtraDataFields
            }
            responseToQuestion(referrerId: $referrerId) @include(if: $includeQuestionResponse) {
              id
              title
              type
              cardId
              ...QuestionGuestList
            }
            responseToSpark {
              ...SparkFields
            }
          }
        }
      }
    }
  }
  ${PREVIEW_CARD}
  ${PREVIEW_IMAGES}
  ${EXTRA_DATA_FIELDS}
  ${PREVIEW_ACTION_USER}
  ${CORE_USER_FIELDS}
`;

export const GET_USER_RECENT_CARD = gql`
  query getUserRecentCard(
    $userId: String!
    $type: CardTypeOptions = CARD
    $sorting: ECardSortingOptions = PICK_DATE_CREATED_DESC
    $excludedIds: [String] = []
    $withImage: Boolean
    $refListId: String
  ) {
    allUsers(filters: { id: $userId }) {
      totalCount
      items {
        id
        username
        cardId
        pickedCards(sorting: $sorting, filters: { type: $type, withImage: $withImage, not: { idIn: $excludedIds } }) {
          totalCount
          items {
            ...PreviewCard
            ...PreviewImages
            comments(limit: 1) {
              totalCount
              items {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_USER_CARDS_ID = gql`
  query getUserCardsId($id: String!, $offset: Int) {
    allUsers(filters: { id: $id }) {
      totalCount
      items {
        id
        username
        cardId
        pickedCards(limit: 50, offset: $offset, filters: { type: CARD }) {
          totalCount
          items {
            id
            type
            cardId
          }
        }
      }
    }
  }
`;

export const GET_ALL_PICKS_COVER = gql`
  query getAllPicksCover($id: String) {
    allUsers(filters: { id: $id }) {
      totalCount
      items {
        id
        username
        cardId
        allPicksCover(limit: 8) {
          ...CoverFields
        }
      }
    }
  }
`;

export const LIST_PREVIEW_CORE = gql`
  fragment ListPreviewCore on CardSchema {
    id
    cardId
    type
    title
    hasNewCollaboratorsCards
    isCollaborative
    isHidden
    isPrivateList
    createdAt
    updatedAt
    description
    cardsFromCollection(limit: 4) {
      totalCount
      items {
        id
        type
        cardId
      }
    }
    cardStyle {
      id
      bgColor {
        id
        colorValue
        gradientStart
      }
      collectionCover(limit: 6) {
        ...CoverFields
      }
      collectionBgColors(isDetail: false) {
        colorValue
        id
        gradientStart
        gradientEnd
      }
      collectionCardsSources {
        sourceDomainName
        id
      }
    }
  }
`;

export const LIST_PREVIEW = gql`
  fragment ListPreview on CardSchema {
    ...ListPreviewCore
    creators(limit: 6, filters: { or: [{ roleName: OWNER }, { roleName: COLLABORATOR }] }) {
      totalCount
      items {
        roleName
        user {
          ...PreviewActionUser
        }
      }
    }
  }
`;

export const GET_PROFILE_LISTS = gql`
  query getProfileLists($id: String!, $offset: Int!, $filters: CardFilter!, $limit: Int = 10) {
    allUsers(filters: { id: $id }) {
      totalCount
      items {
        id
        username
        cardId
        pickedCards(limit: $limit, offset: $offset, filters: $filters, sorting: PICK_DATE_UPDATED_DESC) {
          totalCount
          items {
            ...ListPreview
          }
        }
      }
    }
  }
`;

export const Card_Background = gql`
  fragment CardBackground on CardSchema {
    id
    cardId
    type
    cardStyle {
      id
      bgColor {
        id
        colorValue
        gradientStart
        gradientEnd
      }
    }
  }
`;

export const GET_USER_BACKGROUND_COLOR = gql`
  query getUserBackgroundColor($userId: String) {
    allUsers(filters: { id: $userId }) {
      totalCount
      items {
        id
        pickedCards(limit: 1, filters: { type: CARD }) {
          totalCount
          items {
            ...CardBackground
          }
        }
        spotlights {
          ...CardBackground
        }
      }
    }
  }
`;

export const GET_USER_PICKED_TOPICS = gql`
  query getUserPickedTopics($userId: String!, $limit: Int = 100) {
    allUsers(filters: { id: $userId }) {
      totalCount
      items {
        id
        username
        cardId
        pickedCards(limit: $limit, filters: { type: CARD }) {
          totalCount
          items {
            topicTags: tags(limit: 10, filters: { isEnabled: true }) {
              totalCount
              items {
                id
                name
              }
            }
            cardMediaTags: mediaTags(limit: 10) {
              totalCount
              items {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_FILTERED_USER_PICKS = gql`
  query getFilteredUserPicks(
    $userId: String!
    $offset: Int = 0
    $limit: Int = 10
    $topicIds: [Int]
    $mediaTagIds: [Int]
    $sorting: ECardSortingOptions = PICK_DATE_CREATED_DESC
    $refListId: String
  ) {
    allUsers(filters: { id: $userId }) {
      totalCount
      items {
        id
        username
        cardId
        pickedCards(
          limit: $limit
          offset: $offset
          sorting: $sorting
          filters: { type: CARD, tagIds: $topicIds, mediaTagIds: $mediaTagIds }
        ) {
          totalCount
          items {
            ...PreviewCard
            ...PreviewImages
            topicTags: tags(limit: 5) {
              totalCount
              items {
                id
                name
              }
            }
            cardMediaTags: mediaTags(limit: 5) {
              totalCount
              items {
                id
                name
              }
            }
            extraData(limit: 3) {
              ...ExtraDataFields
            }
          }
        }
      }
    }
  }
  ${PREVIEW_CARD}
  ${PREVIEW_IMAGES}
  ${EXTRA_DATA_FIELDS}
  ${PREVIEW_ACTION_USER}
  ${CORE_USER_FIELDS}
`;
